import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import LoadingSpinner from "../../utils/loading-spinner";
import ConfirmationDialog from "../../utils/confirmation-dialog";

const CollectionClaimId = () => {
  const navigate = useNavigate();
  const { collection } = useParams();
  const config = useSelector((state) => state.config);
  const [claimIds, setClaimIds] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [selectedNotLegitClaim, setSelectedNotLegitClaim] = useState(null);
  const [receiverAddress, setReceiverAddress] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mintError, setMintError] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (!config?.clientConfig?.data?.collections.includes(collection)) {
      navigate("/admin/claim-ids");
    }
  }, [collection, config, navigate]);

  const fetchClaimIds = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/collections-claim-ids/${collection}`,
      );
      setClaimIds(response.data.data);
      setCount(response.data.count);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (collection) {
      fetchClaimIds();
    }
  }, [collection]);

  const handleNotLegitClaim = async (item) => {
    try {
      setLoading(true);
      console.log(
        "process.env.REACT_APP_SERVER_URL",
        process.env.REACT_APP_SERVER_URL,
      );
      console.log("collection", collection);
      console.log("item", item);
      console.log("item.claimId", item.claimId);

      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/collections-claim-ids/${collection}/${item.claimId}`,
        {
          isNotLegitClaim: true,
        },
      );
      if (response.status === 200) {
        await fetchClaimIds();
      }
    } catch (err) {
      console.error(err);
      setError("Error updating claim status");
    } finally {
      setLoading(false);
    }
  };

  const mintNewAsset = (e, item) => {
    e.preventDefault();
    setSelectedClaim(item);
    setIsModalOpen(true);
    setMintError(null);
    setReceiverAddress("");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedClaim(null);
    setReceiverAddress("");
    setMintError(null);
    setIsSubmitting(false);
  };

  const validateEthereumAddress = (address) => {
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  const handleMintSubmit = async (e) => {
    e.preventDefault();

    if (!validateEthereumAddress(receiverAddress)) {
      setMintError("Please enter a valid Ethereum address");
      return;
    }

    setIsSubmitting(true);
    setMintError(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/collections/${collection}/mint-extra`,
        {
          claimId: selectedClaim.claimId,
          receiverAddress: receiverAddress,
        },
      );

      if (response.status === 200) {
        await fetchClaimIds();
        closeModal();
      }
    } catch (err) {
      console.error(err);
      setMintError(err.response?.data?.message || "Error minting new asset");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = () => {
    console.log("Confermato");
    if (selectedNotLegitClaim) {
      handleNotLegitClaim(selectedNotLegitClaim);
    }
    setIsDialogOpen(false);
    setSelectedNotLegitClaim(null);
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
    setSelectedNotLegitClaim(null);
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/collections-claim-ids/export-csv`,
        {
          addresses: [collection],
          collectionAddress: collection,
        },
        { responseType: "blob" },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "claims.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError("Error exporting CSV");
    } finally {
      setLoading(false);
    }
  };

  if (!config?.clientConfig?.data) {
    return <div className="p-4">Loading...</div>;
  }

  if (loading) {
    return <div className="p-4">Loading data...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-600">Error: {error}</div>;
  }

  if (config?.clientConfig?.data?.collections.includes(collection)) {
    return (
      <>
        <div className="overflow-x-auto">
          <div className="px-6 py-1">
            Collection name: {claimIds[0].collectionName}
          </div>
          <div className="px-6 py-1">
            Collection address: {claimIds[0].address}
          </div>
          <div className="px-6 py-1 pb-5">Item count: {count}</div>
          <div className="px-6 py-1 pb-5">
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200"
              disabled={loading}
            >
              {loading ? "Exporting..." : "Export CSV"}
            </button>
          </div>
          <table className="min-w-full bg-white shadow-md rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {claimIds.map((collection) =>
                collection.claims.map((claim, claimIndex) => (
                  <tr
                    key={claim._id}
                    className={claimIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {claim.itemName}
                    </td>
                    <td className="px-6 py-4 font-mono text-sm">
                      {claim.claimId}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          claim.isNotLegitClaim
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {claim.isNotLegitClaim ? "Not Legit" : "Legit"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {!claim.isNotLegitClaim && (
                        <button
                          className="px-4 py-2 rounded mt-4 bg-red-600 text-white hover:bg-red-700 transition-colors duration-200"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedNotLegitClaim(claim);
                            setIsDialogOpen(true);
                          }}
                        >
                          Not legit claim
                        </button>
                      )}
                      {claim.isNotLegitClaim && (
                        <button
                          className="px-4 py-2 rounded mt-4 bg-green-600 text-white hover:bg-green-700 transition-colors duration-200"
                          onClick={(e) => mintNewAsset(e, claim)}
                        >
                          Mint new asset
                        </button>
                      )}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>

        {/* Mint Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Mint New Asset</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Claim Details:</p>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="mb-1">
                    <span className="font-semibold">Item Name:</span>{" "}
                    {selectedClaim?.itemName}
                  </p>
                  <p className="mb-1">
                    <span className="font-semibold">Claim ID:</span>{" "}
                    {selectedClaim?.claimId}
                  </p>
                </div>
              </div>

              <form onSubmit={handleMintSubmit}>
                <div className="mb-4">
                  <label
                    htmlFor="receiverAddress"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Receiver Wallet Address
                  </label>
                  <input
                    type="text"
                    id="receiverAddress"
                    value={receiverAddress}
                    onChange={(e) => setReceiverAddress(e.target.value)}
                    placeholder="0x..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                  {mintError && (
                    <p className="mt-1 text-sm text-red-600">{mintError}</p>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-200 disabled:bg-green-400"
                    disabled={isSubmitting || !receiverAddress}
                  >
                    {isSubmitting ? "Minting..." : "Confirm Mint"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Loading Spinner */}
        <LoadingSpinner
          isLoading={isSubmitting}
          text="Creazione nuovo asset NFT in corso, attendere."
        />
        <ConfirmationDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          title="Conferma azione"
          content="Sei sicuro di voler procedere con questa azione? L'azione è irreversibile"
          showConfirm={true}
          showCancel={true}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </>
    );
  }

  return null;
};

export default CollectionClaimId;
